import { LocaleCode, MarketCode } from '@tink/config/src/markets';
import { ErrorReason, ErrorStatus } from '@tink/core/src/tinkLinkErrorCodes';
import { FormattedMessage } from 'react-intl';
import { connect, ConnectedProps } from 'react-redux';
import { useEffect } from 'react';
import { PageView } from '../../../models/analytics';
import { LegalTextType } from '../../../core/components/ClientLegalDisclaimer/ClientLegalDisclaimer';
import {
  DocumentOverlayLink as DocumentOverlayLinkImport,
  DocumentOverlayLinkProps,
} from '../../../components/screens/molecules/LegalDisclaimer/molecules/DocumentOverlayLink/DocumentOverlayLink';
import { setApplicationError as setApplicationErrorConnect } from '../../../stores/app-state-store';
import { messages } from '../../../components/screens/molecules/LegalDisclaimer/LegalDisclaimerAuth/messages';
import { getLanguageCode } from '../../../utilities/utils';
import { getPrivacyPolicyUrl } from '../../../utilities/client-legal-url';
import { getTermsAndConditionsMarketUrl } from '../../../core/screens/Legal/TermsAndConditions/TermsAndConditionsMarket';
import { getPrivacyPolicyMarketUrl } from '../../../core/screens/Legal/PrivacyPolicy/PrivacyPolicyMarket';

export type LocalizedLegalDocument = {
  locale: LocaleCode;
  termsUrl?: string;
  privacyUrl?: string;
};

type ClientLegalText = {
  type: LegalTextType;
  defaultLocale: LocalizedLegalDocument;
  supportedLocales: LocalizedLegalDocument[];
};

export type LegalMapping = Record<string, ClientLegalText>;

export const clientMapping: LegalMapping = {
  // FTS test app
  d5601dfcfa994e159ca7f98773c58694: {
    type: LegalTextType.INTERNAL,
    defaultLocale: {
      locale: LocaleCode.EN,
      termsUrl: undefined,
      privacyUrl: getPrivacyPolicyUrl('b1a56c2756e64308886690d1408ee715', LocaleCode.EN),
    },
    supportedLocales: [
      {
        locale: LocaleCode.DE,
        termsUrl: undefined,
        privacyUrl: getPrivacyPolicyUrl('b1a56c2756e64308886690d1408ee715', LocaleCode.DE),
      },
      {
        locale: LocaleCode.EN,
        termsUrl: undefined,
        privacyUrl: getPrivacyPolicyUrl('b1a56c2756e64308886690d1408ee715', LocaleCode.EN),
      },
    ],
  },
  // Ayden live app
  c5232a05a5c847c7994bed6765c9a9d1: {
    type: LegalTextType.INTERNAL,
    defaultLocale: {
      locale: LocaleCode.EN,
      termsUrl: undefined,
      privacyUrl: getPrivacyPolicyUrl('b1a56c2756e64308886690d1408ee715', LocaleCode.EN),
    },
    supportedLocales: [
      {
        locale: LocaleCode.DE,
        termsUrl: undefined,
        privacyUrl: getPrivacyPolicyUrl('b1a56c2756e64308886690d1408ee715', LocaleCode.DE),
      },
      {
        locale: LocaleCode.EN,
        termsUrl: undefined,
        privacyUrl: getPrivacyPolicyUrl('b1a56c2756e64308886690d1408ee715', LocaleCode.EN),
      },
    ],
  },
};

export type GetClientLegalMappingsArgs = {
  clientId: string;
  locale: LocaleCode;
  clientMappings?: LegalMapping;
};

export const getClientLegalMappings = ({
  clientId,
  locale,
  clientMappings = clientMapping,
}: GetClientLegalMappingsArgs) => {
  if (!clientMapping[clientId]) return undefined;

  const preferredLocale = clientMappings[clientId].supportedLocales.find(
    (entry) => entry.locale === locale
  );

  if (!preferredLocale) return clientMappings[clientId].defaultLocale;

  return preferredLocale;
};

export const ERROR_CLIENT_LEGAL_DOCS_NOT_CONFIGURED = {
  errorStatus: ErrorStatus.INTERNAL_ERROR,
  errorReason: ErrorReason.CLIENT_LEGAL_DOCS_NOT_CONFIGURED,
};

export type AppConsentClientLegalComponentProps = {
  clientName: string;
  clientId: string;
  locale: LocaleCode;
  market: MarketCode;
  getMappings?: ({
    clientId,
    locale,
    clientMappings,
  }: GetClientLegalMappingsArgs) => LocalizedLegalDocument | undefined;
  DocumentOverlayLink?: React.ComponentType<DocumentOverlayLinkProps>;
  mappings?: LegalMapping;
};

export type AppConsentClientLegalProps = AppConsentClientLegalComponentProps & PropsFromRedux;

export const AppConsentClientLegalPresenter = ({
  clientName,
  clientId,
  locale,
  market,
  getMappings = getClientLegalMappings,
  DocumentOverlayLink = DocumentOverlayLinkImport,
  mappings = clientMapping,
  setApplicationError,
}: AppConsentClientLegalProps) => {
  const urlMap = getMappings({ clientId, locale, clientMappings: mappings });

  useEffect(() => {
    if (!urlMap) {
      setApplicationError(ERROR_CLIENT_LEGAL_DOCS_NOT_CONFIGURED);
    }
  }, [urlMap, setApplicationError]);

  if (!urlMap) {
    return null;
  }
  const languageCode = getLanguageCode(locale);

  return (
    <FormattedMessage
      {...messages.termsAndConditionsMessage}
      values={{
        clientName,
        termsAndConditions: (
          <DocumentOverlayLink
            data-testid="terms-url"
            documentUrl={
              urlMap.termsUrl ?? getTermsAndConditionsMarketUrl({ market, language: languageCode })
            }
            pageView={PageView.TERMS_AND_CONDITION_SCREEN}
          >
            <FormattedMessage {...messages.termsAndConditions} />
          </DocumentOverlayLink>
        ),
        privacyPolicy: (
          <DocumentOverlayLink
            data-testid="privacy-url"
            documentUrl={
              urlMap.privacyUrl ?? getPrivacyPolicyMarketUrl({ market, language: languageCode })
            }
            pageView={PageView.PRIVACY_POLICY_SCREEN}
          >
            <FormattedMessage {...messages.privacyPolicy} />
          </DocumentOverlayLink>
        ),
      }}
    />
  );
};

const connector = connect(undefined, {
  setApplicationError: setApplicationErrorConnect,
});

type PropsFromRedux = ConnectedProps<typeof connector>;

export const AppConsentClientLegal = connector(AppConsentClientLegalPresenter);
